import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { SimulatorEstimateDto } from 'used-vehicle-contracts';
import { DEPRECIATION_CATEGORIES, describeChange } from 'used-vehicle-engine';
import { orpc } from '../../apis/orpc-query';
import { money, pct, stamp } from './format';

const categoryName = (key: string): string => DEPRECIATION_CATEGORIES.find((category) => category.key === key)?.name ?? key;

const errorMargin = (estimate: SimulatorEstimateDto): number | null =>
  estimate.preownedPrice && estimate.preownedPrice > 0
    ? Math.abs((estimate.outputs.simulatorPrice - estimate.preownedPrice) / estimate.preownedPrice)
    : null;

/** Every evaluation saved from the simulator, newest first, with the figures the review sheet prints. */
export const SavedEstimatesPage = () => {
  const queryClient = useQueryClient();
  const list = useQuery(orpc.simulator.list.queryOptions());
  const remove = useMutation(orpc.simulator.remove.mutationOptions());
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onRemove = async (estimate: SimulatorEstimateDto): Promise<void> => {
    if (!window.confirm(`Remove the saved estimate for ${estimate.inputs.brand} ${estimate.inputs.model}?`)) {
      return;
    }

    setRemoving(estimate.id);
    setError(null);

    try {
      await remove.mutateAsync({ id: estimate.id });
      await queryClient.invalidateQueries({ queryKey: orpc.simulator.list.key() });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The estimate could not be removed.');
    } finally {
      setRemoving(null);
    }
  };

  const items = list.data?.items ?? [];

  return (
    <section id="saved">
      <div className="toolbar">
        <span className="spacer" />
        {list.isLoading && <span className="notice">Loading…</span>}
        {list.isError && <span className="notice err">The saved estimates could not be loaded.</span>}
        {error && <span className="notice err">{error}</span>}
      </div>
      {list.isSuccess && items.length === 0 && (
        <p className="empty">
          Nothing saved yet. Price a car on the simulator and use “Save this estimate”; every saved row lands here and in the export.
        </p>
      )}
      {items.length > 0 && (
        <div className="table-wrap list">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Saved</th>
                <th>Brand</th>
                <th>Model</th>
                <th>VIN</th>
                <th>Customer</th>
                <th>Deal owner</th>
                <th>Category</th>
                <th>New price</th>
                <th>Simulator</th>
                <th>Preowned</th>
                <th>Error margin</th>
                <th>Changed factors</th>
                <th>Remarks</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((estimate, index) => {
                const margin = errorMargin(estimate);

                return (
                  <tr key={estimate.id}>
                    <td className="n">{items.length - index}</td>
                    <td className="lbl">{stamp(estimate.createdAt)}</td>
                    <td className="lbl">{estimate.inputs.brand}</td>
                    <td className="lbl">
                      {estimate.inputs.model}
                      <small>
                        {estimate.inputs.age} {estimate.inputs.age === 1 ? 'year' : 'years'} · {money(estimate.inputs.odometer)} km
                      </small>
                    </td>
                    <td className="n">{estimate.vin || '—'}</td>
                    <td className="lbl">{estimate.customerName || '—'}</td>
                    <td className="lbl">{estimate.dealOwner ?? '—'}</td>
                    <td className="lbl">{categoryName(estimate.inputs.cat)}</td>
                    <td className="n">{money(estimate.inputs.price)}</td>
                    <td className="n">
                      {money(estimate.outputs.simulatorPrice)}
                      <small>{pct(estimate.outputs.left)}</small>
                    </td>
                    <td className="n">{estimate.preownedPrice !== null ? money(estimate.preownedPrice) : '—'}</td>
                    <td className="n">{margin !== null ? pct(margin, 1) : '—'}</td>
                    <td
                      className={`lbl${estimate.changes.length ? ' flag' : ''}`}
                      title={estimate.changes.map(describeChange).join('\n')}
                    >
                      {estimate.changes.length ? `${estimate.changes.length} changed` : 'defaults'}
                      {estimate.changes.length > 0 && <small>{estimate.changes.map(describeChange).join(' · ')}</small>}
                    </td>
                    <td className="remarks">{estimate.remarks ?? ''}</td>
                    <td>
                      <button
                        type="button"
                        className="plain"
                        disabled={removing === estimate.id}
                        onClick={() => void onRemove(estimate)}
                      >
                        {removing === estimate.id ? 'Removing…' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="table-note">
        The export lists these rows oldest first, in the review template’s columns, with the error margin as a live formula and every changed factor
        flagged in amber.
      </p>
    </section>
  );
};
